import React, { useEffect, useRef, useState } from 'react'
import './NotificationSummary.scss'
import {
  ActionStatus,
  INotification,
  INotificationFilter,
  NotificationStatus,
  NotificationType,
} from 'types'
import { beautifyNotificationDate } from 'utils/notificationUtils'
import { useNotificationContext } from 'containers/notifications/NotificationContext'
import { useLocation } from 'react-router-dom'
import useElementOnScreen from 'containers/useElementOnScreen'
import NotificationList from './NotificationList'

type ViewMode = 'comfortable' | 'compact'

const NotificationSummary = ({ currentOrganization }) => {
  const [selectedFilter, setSelectedFilter] = useState<NotificationStatus>(
    NotificationStatus.All
  )
  const [selectedPreview, setSelectedPreview] = useState<INotification>()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [viewMode, setViewMode] = useState<ViewMode>('comfortable')
  const {
    notifications,
    notificationCursor,
    updateNotification,
    nextNotifications,
  } = useNotificationContext()
  const { elementRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '10px',
    threshold: 1.0,
  })
  const location = useLocation()

  const handleClickFilter = (e) => {
    setSelectedFilter(Number(e.target.dataset.filterId))
  }

  const handleClickNotification = (notification: INotification) => {
    const targetNotification = notifications?.find(
      (notificationInstance) => notificationInstance.id === notification.id
    )
    setSelectedPreview(targetNotification)
    if (
      notification.type === NotificationType.Persistent &&
      notification.action?.status !== ActionStatus.FULFILLED
    ) {
      return
    } else {
      handleMarkAsReadNotification(notification)
    }
  }

  const handleMarkAsReadNotification = (notification: INotification) => {
    if (notification.status !== NotificationStatus.Unread) {
      return
    }
    const updateParams: INotificationFilter = {
      notificationId: notification.id,
      status: NotificationStatus.Read,
    }
    if (updateNotification) updateNotification(updateParams)
  }

  const handleArchiveNotification = (
    archive: boolean = true,
    notification: INotification
  ) => {
    const updateParams: INotificationFilter = {
      notificationId: notification.id,
      status: archive ? NotificationStatus.Archived : NotificationStatus.Read,
    }
    if (updateNotification) updateNotification(updateParams)
  }

  const handlePinNotification = (pin: boolean, notification: INotification) => {
    const updateParams: INotificationFilter = {
      notificationId: notification.id,
      pinned: pin,
    }
    if (updateNotification) updateNotification(updateParams)
  }

  useEffect(() => {
    // in case we switch organization we want to remove the previous preview
    setSelectedPreview(undefined)
  }, [currentOrganization])

  // check if there's state for selectedPreview
  useEffect(() => {
    // @ts-ignore
    const statePreview = location.state?.selectedPreview
    if (statePreview !== undefined) {
      setSelectedPreview(statePreview)
    }
  }, [location])

  useEffect(() => {
      if (isVisible) {
        console.log('visible')
        if (nextNotifications) nextNotifications()
      }
  }, [isVisible])

  // Filter notifications based on search query
  const filteredNotifications = React.useMemo(() => {
    if (!notifications) return null

    if (!searchQuery.trim()) {
      return notifications
    }

    const query = searchQuery.toLowerCase()
    return notifications.filter((notification) => {
      const titleMatch = notification.title?.toLowerCase().includes(query)
      const bodyMatch = notification.body?.toLowerCase().includes(query)
      return titleMatch || bodyMatch
    })
  }, [notifications, searchQuery])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleToggleViewMode = (mode: ViewMode) => {
    setViewMode(mode)
  }

  return (
    <div className="notification-page">
      <div className="notification-page-header">
        <h1>Notifications</h1>
        <div className="header-controls">
          <div className="search-bar">
            <span className="material-icons-outlined search-icon">search</span>
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            )}
          </div>
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'comfortable' ? 'active' : ''}`}
              onClick={() => handleToggleViewMode('comfortable')}
              data-tooltip="Comfortable view"
              aria-label="Comfortable view"
            >
              <span className="material-icons-outlined">view_agenda</span>
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
              onClick={() => handleToggleViewMode('compact')}
              data-tooltip="Compact view"
              aria-label="Compact view"
            >
              <span className="material-icons-outlined">view_headline</span>
            </button>
          </div>
        </div>
      </div>
      <div className="notification-preview">
        {selectedPreview !== null && selectedPreview !== undefined && (
          <>
            <div className="notification-preview__header">
              <p className="notification-preview__title">
                {selectedPreview.title}
              </p>
              <p className="notification-preview__date">
                Sent on {beautifyNotificationDate(selectedPreview.created_at)}
              </p>
            </div>
            <div className="notification-preview__body">
              {selectedPreview.body}
            </div>
          </>
        )}
      </div>
      <div className={`notification-wrapper ${viewMode}`}>
        <div className="notification-filter-container">
          <ul className="notification-filter-list">
            <li
              className="notification-filter"
              data-filter-id={NotificationStatus.All}
              data-selected={selectedFilter === NotificationStatus.All}
              onClick={handleClickFilter}
            >
              all
            </li>
            <li
              className="notification-filter"
              data-filter-id={NotificationStatus.Unread}
              data-selected={selectedFilter === NotificationStatus.Unread}
              onClick={handleClickFilter}
            >
              unread
            </li>
            <li
              className="notification-filter"
              data-filter-id={NotificationStatus.Read}
              data-selected={selectedFilter === NotificationStatus.Read}
              onClick={handleClickFilter}
            >
              read
            </li>
            <li
              className="notification-filter"
              data-filter-id={NotificationStatus.Archived}
              data-selected={selectedFilter === NotificationStatus.Archived}
              onClick={handleClickFilter}
            >
              archived
            </li>
          </ul>
        </div>
        <div className="notification-container">
          <NotificationList
            handleArchiveNotification={handleArchiveNotification}
            handleClickNotification={handleClickNotification}
            handlePinNotification={handlePinNotification}
            notifications={filteredNotifications}
            selectedFilter={selectedFilter}
            triggerElementRef={elementRef}
            hasMoreNotifications={notificationCursor !== null}
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(NotificationSummary)
